import type { MetricSeries } from '@bartizo/shared';
import { useQuery } from '@tanstack/react-query';
import { useState, type ReactNode } from 'react';
import { Link } from 'react-router';
import { getMetricSeries } from '../api/metrics.js';
import { useCurrentUser } from '../auth/session-context.js';
import { Alert } from '../ui/Alert.js';
import { Button } from '../ui/Button.js';
import { Card } from '../ui/Card.js';
import { DECISIONS_METRIC, ORIGIN_LABEL, ORIGINS } from '../lib/crowdsec.js';
import { ActionDialog, type ActionDialogServer } from './ActionDialog.js';
import { MetricChart } from './MetricChart.js';

/**
 * CrowdSec, on a server's page (knowledge doc sections 8.1 and 12).
 *
 * What is being blocked here and by whom — the `crowdsec_active_decisions_count`
 * metric the module sends every poll, one line per origin — and, for an operator,
 * the two things section 12 lets them do about it: ban an address and unban one.
 * Both open the same two-step dialog; the results are on the action's own page.
 *
 * A server whose heartbeat says CrowdSec is not installed gets a sentence instead
 * of buttons: the API would skip it anyway, and a button that can only fail is
 * worse than none.
 */

const count = (value: number): string => Math.round(value).toLocaleString('en-US');

const latest = (series: readonly MetricSeries[], origin: string): number | undefined =>
  series.find((line) => line.labels['origin'] === origin)?.points.at(-1)?.avg;

export function CrowdsecCard({
  server,
  installed,
  timezone,
}: {
  server: ActionDialogServer;
  /** `capabilities.crowdsec.installed`; undefined until a heartbeat has said. */
  installed: boolean | undefined;
  timezone: string;
}): ReactNode {
  const session = useCurrentUser();
  const canAct = session.user.role !== 'viewer';
  const [dialog, setDialog] = useState<'ban_ip' | 'unban_ip' | null>(null);

  const decisions = useQuery({
    queryKey: ['metrics', server.id, DECISIONS_METRIC, 24],
    queryFn: () =>
      getMetricSeries({
        serverId: server.id,
        name: DECISIONS_METRIC,
        from: new Date(Date.now() - 24 * 3_600_000).toISOString(),
      }),
    enabled: installed === true,
  });

  if (installed === undefined) return null;

  const series = decisions.data?.series ?? [];

  return (
    <Card
      title="CrowdSec"
      description="Addresses blocked on this server right now, by who decided to block them."
      aside={
        installed &&
        canAct && (
          <span className="row">
            <Button
              variant="danger"
              onClick={() => {
                setDialog('ban_ip');
              }}
            >
              Ban an address
            </Button>
            <Button
              onClick={() => {
                setDialog('unban_ip');
              }}
            >
              Unban
            </Button>
          </span>
        )
      }
      footer={
        <Link to={`/actions?server_id=${encodeURIComponent(server.id)}`} className="text-sm">
          Bans and unbans on this server
        </Link>
      }
    >
      {!installed ? (
        <p className="muted text-sm">
          CrowdSec is not installed on this server, so nothing is blocked here and bans skip it. The
          installer adds it unless it was run with <code>--no-crowdsec</code>.
        </p>
      ) : (
        <>
          {decisions.isError && (
            <Alert tone="danger" title="Could not load the decision counts">
              Try again in a moment.
            </Alert>
          )}
          {decisions.data !== undefined && series.length === 0 && (
            <p className="muted text-sm">
              Nothing reported yet. The agent counts CrowdSec’s decisions every minute.
            </p>
          )}
          {series.length > 0 && decisions.data !== undefined && (
            <>
              <dl className="details">
                {ORIGINS.map((origin) => (
                  <div key={origin} style={{ display: 'contents' }}>
                    <dt>{ORIGIN_LABEL[origin]}</dt>
                    <dd>{count(latest(series, origin) ?? 0)}</dd>
                  </div>
                ))}
              </dl>
              {/* Without the community lists: tens of thousands of addresses on the
                  same axis would flatten every other line to zero. Their count is
                  above, and their changes are the hourly summary event. */}
              <MetricChart
                title="Decisions in force, last day"
                series={series.filter((line) => line.labels['origin'] !== 'community')}
                from={decisions.data.from}
                to={decisions.data.to}
                bucketSeconds={decisions.data.bucketSeconds}
                format={count}
                seriesLabel={(line) => ORIGIN_LABEL[line.labels['origin'] ?? 'other'] ?? 'Other'}
                yMin={0}
                timezone={timezone}
              />
            </>
          )}
        </>
      )}

      {dialog !== null && (
        <ActionDialog
          type={dialog}
          server={server}
          onClose={() => {
            setDialog(null);
          }}
        />
      )}
    </Card>
  );
}
